import { useMemo } from "react";
import { i18nCrossUtil } from "@adgm/libs";
import type { TInstitutionBeneficiary } from "@adgm/cross-models";

/**
 * 受益人数据接口
 */
export interface IBeneficiaryData extends TInstitutionBeneficiary {
  id?: string;
}

/**
 * 组件 Props 接口
 */
export interface IKycBeneficiariesTableProps {
  value: IBeneficiaryData[];
  onChange?: (beneficiaries: IBeneficiaryData[]) => void;
  defaultValue?: IBeneficiaryData[];
}

/**
 * 列配置创建器类型
 */
export type TColumnConfigCreator<T = any> = (type: string, options?: any) => T;

/**
 * hook 返回值接口
 */
export interface IUseKycBeneficiariesTableReturn<TColumnConfig = any> {
  /** 当前数据 */
  beneficiaries: IBeneficiaryData[];
  /** 列配置 */
  beneficiaryColumns: TColumnConfig[];
  /** 数据变化处理函数 */
  handleChange: (data: any[]) => void;
  /** 国际化对象 */
  crossIntl: any;
}

/**
 * KYC 受益人表格公共逻辑 Hook
 *
 * @param props - 组件props
 * @param createRenderer - 渲染器创建函数
 * @returns 表格所需的所有逻辑和数据
 */
export function useKycBeneficiariesTable<TColumnConfig = any>(
  props: IKycBeneficiariesTableProps,
  createRenderer: TColumnConfigCreator,
): IUseKycBeneficiariesTableReturn<TColumnConfig> {
  const { value, onChange } = props;
  const crossIntl = i18nCrossUtil.t();
  // 定义受益人表格的列配置
  const beneficiaryColumns = [
    {
      key: "uboName",
      title: crossIntl["kyc.institution.basicInfo.owner.name.label"],
      dataIndex: "uboName",
      required: true,
      type: "text",
      render: createRenderer("text", { maxLength: 500 }),
    },
    {
      key: "shareholdingPercentage",
      title: crossIntl["kyc.institution.basicInfo.owner.percentage.label"],
      dataIndex: "shareholdingPercentage",
      required: true,
      type: "percentage" as const,
      render: createRenderer("percentage"),
    },
    {
      key: "nationalities",
      title: crossIntl["kyc.institution.basicInfo.owner.nationality.label"],
      dataIndex: "nationalities",
      required: true,
      type: "nationality" as const,
      render: createRenderer("nationality"),
    },
    {
      key: "residentialAddress",
      title: crossIntl["kyc.v2.display.label.residential.address"],
      dataIndex: "residentialAddress",
      required: true,
      type: "text" as const,
      render: createRenderer("text"),
    },
  ];

  const beneficiariesValue = useMemo(() => {
    return value.map(item => {
      return {
        ...item,
      };
    });
  }, [value]);

  const handleChange = (data: any[]) => {
    // 转换回受益人数据格式
    const beneficiaries = data.map(item => {
      return {
        uboName: item.uboName || "",
        shareholdingPercentage: item.shareholdingPercentage || "",
        nationalities: item.nationalities || "",
        residentialAddress: item.residentialAddress || "",
      };
    });
    onChange?.(beneficiaries);
  };

  return {
    beneficiaries: beneficiariesValue,
    beneficiaryColumns: beneficiaryColumns as TColumnConfig[],
    handleChange,
    crossIntl,
  };
}
